import calculateMidColor, { hexToRgbA } from '@/utilities/colors';
import { getTheme } from '@/utilities/lightTheme';
import type { Theme } from '@simplywallst/ui-core';
import { getCSSVar } from '@simplywallst/ui-core/next';

const SECTIONS = ['value', 'future', 'past', 'health', 'dividend'] as const;
export type SnowflakeSection = (typeof SECTIONS)[number];

const flakeMinColour = '#FF2B2B';
const flakeMaxColour = '#00E613';
const snowflakeRingEven = '#2D3642';
const snowflakeRingOdd = '#424B58';

/**
 * Blob width with respect to the canvas width, if labels are
 * active (aka if labels are visible, the blob width will equal
 * 0.7766785714 of the full width of the canvas)
 */
const WIDTH_RATIO = 0.7766785714;

// Calculates the bezier control points for segment (x1,y1)-(x2,y2)
export const calculateControlPoints = (
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  tensor: number
) => {
  const len1 = Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0));
  const len2 = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
  const len3 = Math.sqrt((x3 - x2) * (x3 - x2) + (y3 - y2) * (y3 - y2));

  const k1 = len1 / (len1 + len2);
  const k2 = len2 / (len2 + len3);

  const xc1 = (x0 + x1) / 2.0;
  const yc1 = (y0 + y1) / 2.0;
  const xc2 = (x1 + x2) / 2.0;
  const yc2 = (y1 + y2) / 2.0;
  const xc3 = (x2 + x3) / 2.0;
  const yc3 = (y2 + y3) / 2.0;

  const xm1 = xc1 + (xc2 - xc1) * k1;
  const ym1 = yc1 + (yc2 - yc1) * k1;

  const xm2 = xc2 + (xc3 - xc2) * k2;
  const ym2 = yc2 + (yc3 - yc2) * k2;

  const cx1 = xm1 + (xc2 - xm1) * tensor + x1 - xm1;
  const cy1 = ym1 + (yc2 - ym1) * tensor + y1 - ym1;

  const cx2 = xm2 + (xc2 - xm2) * tensor + x2 - xm2;
  const cy2 = ym2 + (yc2 - ym2) * tensor + y2 - ym2;

  return [cx1, cy1, cx2, cy2];
};

type BullsEyeFillProps = {
  isEven: boolean;
  color: Theme['color'];
  light: boolean | undefined;
  dark: boolean | undefined;
  disableLabel: boolean | undefined;
};

const bullsEyeFill = ({
  isEven,
  color,
  light,
  dark,
  disableLabel,
}: BullsEyeFillProps) => {
  /**
   * Temporary hardcode for even and odd rings colours until a better
   * design can be achieve by changing chart implementation
   */

  if (isEven) {
    if (disableLabel) {
      return '#27313D';
    } else {
      // To keep legacy behaviour, only optionally use the theme variable
      if (getTheme() !== undefined) {
        return getCSSVar('--s-chart-snowflake-ring-even') ?? color.spaceGray;
      }
      return light || dark ? color.spaceGray : snowflakeRingEven;
    }
  } else {
    if (disableLabel) {
      return '#344352';
    } else {
      if (light) {
        return 'white';
      } else {
        // To keep legacy behaviour, only optionally use the theme variable
        if (getTheme() !== undefined) {
          return getCSSVar('--s-chart-snowflake-ring-odd') ?? color.darkerGray;
        }
        return dark ? color.darkerGray : snowflakeRingOdd;
      }
    }
  }
};

type HighlightSectionProps = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  highlightedSection: SnowflakeSection | undefined;
  disableLabel: boolean | undefined;
};

const highlightSection = ({
  canvas,
  ctx,
  highlightedSection,
  disableLabel,
}: HighlightSectionProps) => {
  if (!highlightedSection) return;

  //Create temporary canvas to copy to
  let tempCanvas: HTMLCanvasElement | null = document.createElement('canvas');
  const { width, height } = canvas;
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx || width === 0 || height === 0) return;
  //Clip the highlighted section
  const PI = Math.PI;
  const diameter = width * (disableLabel ? 0.95 : WIDTH_RATIO);
  const extraPadding = 12; //Extra radius of the circle to cover blobs that overflow the edges
  const index = SECTIONS.indexOf(highlightedSection);
  const startAngle = -PI / 2 + ((2 * PI) / 5) * (index - 0.5);
  const endAngle = -PI / 2 + ((2 * PI) / 5) * (index + 0.5);
  const center = [width / 2, height / 2];
  tempCtx.beginPath();
  tempCtx.arc(
    center[0],
    center[1],
    diameter / 2 + extraPadding,
    startAngle,
    endAngle
  );
  tempCtx.lineTo(center[0], center[1]);
  tempCtx.closePath();
  tempCtx.clip();
  // Scale the highlighted section
  tempCtx.translate(center[0], center[1]);
  tempCtx.scale(1.05, 1.05);
  tempCtx.translate(-center[0], -center[1]);
  // Draw scaled section
  tempCtx.drawImage(canvas, 0, 0);

  // Darken the rest of the circle in original image
  ctx.save();
  ctx.fillStyle = 'rgb(0,0,0,0.4)';
  ctx.beginPath();
  ctx.arc(
    center[0],
    center[1],
    diameter / 2 + extraPadding,
    endAngle,
    startAngle
  );
  ctx.globalCompositeOperation = 'source-atop';
  ctx.lineTo(center[0], center[1]);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // Draw the highlighted section back in with shadows
  ctx.save();
  ctx.shadowBlur = 8;
  ctx.shadowColor = 'rgb(0,0,0,0.4)';
  ctx.drawImage(tempCanvas, 0, 0);
  ctx.restore();

  // Cleans up canvas element and variable that is no longer used, prevents memory leak
  tempCanvas.width = 0;
  tempCanvas.height = 0;
  try {
    tempCanvas.remove();
  } catch (e) {
    document.body?.removeChild(tempCanvas);
  }
  tempCanvas = null;
};

type PaintSnowflakeOnCanvasProps = {
  canvas: HTMLCanvasElement;
  theme: Theme;
};

export type PaintSnowflakeOnCanvasArgs = {
  points: number[];
  size: number;
  disableLabel: boolean | undefined;
  disabled: boolean | undefined;
  light: boolean | undefined;
  dark: boolean | undefined;
  highlightedSection: SnowflakeSection | undefined;
};

interface Point {
  x: number;
  y: number;
}

export const paintSnowflakeOnCanvas = (
  { canvas, theme }: PaintSnowflakeOnCanvasProps,
  {
    points,
    size,
    disableLabel,
    disabled,
    light,
    dark,
    highlightedSection,
  }: PaintSnowflakeOnCanvasArgs
) => {
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  const { color } = theme;

  const width = size;
  const height = size;
  const axisLineWidth = 3 / 280;
  const PI = Math.PI;
  const diameter = width * (disableLabel ? 0.95 : WIDTH_RATIO);
  const arr: Point[] = [];
  const nodeCount = points.length;
  const maxSum = 30;
  const minSum = 5;
  const sum: number = points.reduce((a, b) => +a + (+b - 1), 0);
  const colour = 1 - (sum - minSum) / (maxSum - minSum);
  // null check;
  if (nodeCount === 0) return;
  let max = 7;
  let flakeColorValue = 0;

  flakeColorValue = Math.max(colour, 0);
  flakeColorValue = Math.min(colour, 1);

  for (let i = 0; i < nodeCount; i++) {
    max = Math.max(max, points[i]);
  }

  for (let i = 0; i < nodeCount; i++) {
    const radius = ((diameter / 2) * points[i]) / max;
    const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = width / 2 + Math.sin(angle) * radius;
    arr.push({ x: x, y: y });
  }
  ctx.clearRect(0, 0, width, height);
  ctx.translate(0, 0);

  // Draw bullseye arcs
  for (let i = max; i > 1; i--) {
    ctx.beginPath();
    ctx.fillStyle = bullsEyeFill({
      isEven: i % 2 !== 0,
      color,
      dark,
      light,
      disableLabel,
    });
    ctx.arc(width / 2, height / 2, (i / max) * (diameter / 2), 0, 2 * PI);
    ctx.fill();
  }
  // Draw axis lines
  for (let i = 5; i > 0; i--) {
    const radius = diameter / 2;
    const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = width / 2 + Math.sin(angle) * radius;
    ctx.beginPath();
    // To keep legacy behaviour, only optionally use the theme variable
    if (getTheme() !== undefined && typeof window !== 'undefined') {
      ctx.strokeStyle =
        getCSSVar('--s-chart-snowflake-ring-odd') ?? color.darkerGray;
    } else {
      ctx.strokeStyle = light
        ? 'white'
        : dark
          ? color.darkerGray
          : snowflakeRingOdd;
    }
    ctx.lineWidth = width * axisLineWidth;
    ctx.moveTo(width / 2, height / 2);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  // Draw blob
  ctx.beginPath();
  ctx.strokeStyle = disabled
    ? color.brand03RGBA(0.8)
    : calculateMidColor(flakeMaxColour, flakeMinColour, flakeColorValue, 1);
  ctx.fillStyle = disabled
    ? hexToRgbA(color.brand01, 0.5)
    : calculateMidColor(flakeMaxColour, flakeMinColour, flakeColorValue, 0.7);
  ctx.lineWidth = 4;
  ctx.moveTo(arr[0].x, arr[0].y);

  for (let i = 0; i < nodeCount; i++) {
    const x0 = arr[(i - 1 + nodeCount) % nodeCount].x;
    const y0 = arr[(i - 1 + nodeCount) % nodeCount].y;
    const x1 = arr[i].x;
    const y1 = arr[i].y;
    const x2 = arr[(i + 1) % nodeCount].x;
    const y2 = arr[(i + 1) % nodeCount].y;
    const x3 = arr[(i + 2) % nodeCount].x;
    const y3 = arr[(i + 2) % nodeCount].y;
    const bezier = calculateControlPoints(x0, y0, x1, y1, x2, y2, x3, y3, 1);
    ctx.bezierCurveTo(
      bezier[0],
      bezier[1],
      bezier[2],
      bezier[3],
      arr[(i + 1) % nodeCount].x,
      arr[(i + 1) % nodeCount].y
    );
  }
  ctx.fill();
  ctx.stroke();
  // Draw labels or dots
  if (disableLabel) {
    for (let i = 0; i < nodeCount; i++) {
      const radius = diameter / 2;
      const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
      const x = width / 2 + Math.cos(angle) * radius;
      const y = width / 2 + Math.sin(angle) * radius;
      ctx.beginPath();
      ctx.fillStyle = 'white';
      ctx.arc(x, y, 0.02 * width, 0, 2 * PI);
      ctx.fill();
    }
  }

  // Do any additional modifications
  highlightSection({ canvas, ctx, disableLabel, highlightedSection });

  ctx.save();
  ctx.restore();
};
